<script setup lang="ts">
import { computed } from 'vue';
import { Guest } from '@/types/guest';

const props = defineProps<{
  guests: Guest[];
}>();

const totalGuests = computed(() => props.guests.length);

const guestStats = computed(() => {
  const stats = {
    confirmed: 0,
    declined: 0,
    pending: 0,
  };

  props.guests.forEach((guest) => {
    if (guest.presence === true) stats.confirmed++;
    else if (guest.presence === false) stats.declined++;
    else stats.pending++;
  });

  return stats;
});
</script>

<template>
  <div class="guest-stats">
    <div class="guest-stats__card guest-stats__card--total">
      <div class="guest-stats__content">
        <div class="guest-stats__icon-wrapper guest-stats__icon-wrapper--total">
          <i class="pi pi-users guest-stats__icon"></i>
        </div>
        <div class="guest-stats__info">
          <div class="guest-stats__title">Wszyscy goście</div>
          <div class="guest-stats__value">{{ totalGuests }}</div>
          <div class="guest-stats__label">spośród liczby zaproszonych gości</div>
        </div>
      </div>
    </div>

    <div class="guest-stats__card guest-stats__card--confirmed">
      <div class="guest-stats__content">
        <div class="guest-stats__icon-wrapper guest-stats__icon-wrapper--confirmed">
          <i class="pi pi-check-circle guest-stats__icon"></i>
        </div>
        <div class="guest-stats__info">
          <div class="guest-stats__title">Potwierdzili</div>
          <div class="guest-stats__value">{{ guestStats.confirmed }}</div>
          <div class="guest-stats__label">spośród wszystkich gości</div>
        </div>
      </div>
    </div>

    <div class="guest-stats__card guest-stats__card--declined">
      <div class="guest-stats__content">
        <div class="guest-stats__icon-wrapper guest-stats__icon-wrapper--declined">
          <i class="pi pi-times-circle guest-stats__icon"></i>
        </div>
        <div class="guest-stats__info">
          <div class="guest-stats__title">Odmówili</div>
          <div class="guest-stats__value">{{ guestStats.declined }}</div>
          <div class="guest-stats__label">spośród wszystkich gości</div>
        </div>
      </div>
    </div>

    <div class="guest-stats__card guest-stats__card--pending">
      <div class="guest-stats__content">
        <div class="guest-stats__icon-wrapper guest-stats__icon-wrapper--pending">
          <i class="pi pi-clock guest-stats__icon"></i>
        </div>
        <div class="guest-stats__info">
          <div class="guest-stats__title">Oczekujący</div>
          <div class="guest-stats__value">{{ guestStats.pending }}</div>
          <div class="guest-stats__label">spośród wszystkich gości</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.guest-stats {
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;

  &__card {
    flex: 1;
    background-color: $white;
    border-radius: 12px;
    padding: 2rem;
    position: relative;
    border: 1px solid rgba($black-100, 0.1);
    transition: all 0.3s ease;
    overflow: hidden;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba($black-100, 0.1);
    }

    &--total {
      background: linear-gradient(to right, rgba($status-yellow, 0), rgba($status-yellow, 0.1));
      border-color: rgba($status-yellow, 0.3);
    }

    &--confirmed {
      background: linear-gradient(to right, rgba($status-green, 0), rgba($status-green, 0.1));
      border-color: rgba($status-green, 0.3);
    }

    &--declined {
      background: linear-gradient(to right, rgba($status-red, 0), rgba($status-red, 0.1));
      border-color: rgba($status-red, 0.3);
    }

    &--pending {
      background: linear-gradient(to right, rgba($status-yellow, 0), rgba($status-yellow, 0.1));
      border-color: rgba($status-yellow, 0.3);
    }
  }

  &__content {
    display: flex;
    align-items: flex-start;
    gap: 1.5rem;
  }

  &__info {
    flex: 1;
  }

  &__label {
    color: $black-400;
    font-size: 1.2rem;
    margin-top: 0.25rem;
  }

  &__value {
    color: $black-300;
    font-size: 2.5rem;
    font-weight: bold;
    line-height: 1;
  }

  &__icon-wrapper {
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    &--total {
      background-color: $status-yellow-light;
      color: $status-yellow;
    }

    &--confirmed {
      background-color: $white;
      color: $status-green;
    }

    &--declined {
      background-color: $white;
      color: $status-red;
    }

    &--pending {
      background-color: $white;
      color: $status-yellow;
    }
  }

  &__icon {
    font-size: 1.5rem;
  }

  &__title {
    color: $black-300;
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  @media (max-width: $media-max-phone) {
    flex-direction: column;

    &__card {
      width: 100%;
    }
  }
}
</style>
